import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  UCPList,
  Overview,
  Notes,
  Snapshot,
  Garage,
  Appointments,
  RepairsOrders,
  Tasks,
  TestEmail,
} from "./pages";
import { UCPPageContainer, ContainerWrapper, UCPChatWrapper } from "./UCPPage.styled";
import { BreadcrumbContainer, Navigator } from "./components";
import ChatSlideOut from '../HomePageV2/components/ChatSlideOut';
import ChatErrorBoundary from '../HomePageV2/components/ChatSlideOut/components/ChatErrorBoundary';
import { useHomeChatActions } from '../HomePageV2/context';
import { useChatContextInjector } from '../HomePageV2/hooks';
import { SCOPE_UCP } from '../HomePageV2/constants/chatGraphCodes';
import SectionShell from "./components/SectionShell";

const UCPPage = () => {
  const [isChatOpen, setIsChatOpen] = useState(true);
  // Mount-once: once the chat is opened for the first time, keep it in the DOM.
  const [hasOpenedChat, setHasOpenedChat] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { registerChatHost, unregisterChatHost, openGlobalChat, clearConversationSelection, cleanThread } = useHomeChatActions();

  const { contactId } = useParams();

  const sectionFromPath = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const ucpIdx = parts.indexOf('ucp');
    if (ucpIdx === -1 || !contactId) return 'overview';
    const contactIdx = parts.indexOf(contactId);
    if (contactIdx === -1) return 'overview';
    return parts[contactIdx + 1] || 'overview';
  }, [location.pathname, contactId]);

  const contextEntry = useMemo(() => {
    if (!contactId) return null;
    return {
      key: 'ucp',
      scope: SCOPE_UCP,
      location: { page: 'ucp', section: sectionFromPath },
      ref_entity: 'CONTACT',
      ref_entity_uuid: contactId,
    };
  }, [contactId, sectionFromPath]);

  useChatContextInjector(contextEntry);

  const handleOpenInHome = useCallback(() => {
    navigate('/home');
    setIsChatOpen(false);
  }, [navigate]);

  // Open the chat panel without clearing. Conversation persists when reopening
  // or when navigating within the same contact (overview ↔ notes ↔ etc.).
  // Clearing only happens when switching contacts (A → B), handled by the useEffect.
  const openUCPChat = useCallback(() => {
    setHasOpenedChat(true);
    setIsChatOpen(true);
  }, []);

  // Only clear when switching contacts (A → B). Preserve conversation when
  // arriving from Dashboard/Home or from UCP list (Story 15: same behavior as Home).
  const prevContactIdRef = useRef(null);

  useEffect(() => {
    if (!contactId) return;
    const isContactSwitch = prevContactIdRef.current != null && prevContactIdRef.current !== contactId;
    prevContactIdRef.current = contactId;
    if (isContactSwitch) {
      clearConversationSelection();
      cleanThread();
    }
    setHasOpenedChat(true);
    setIsChatOpen(true);
  }, [contactId, clearConversationSelection, cleanThread]);

  // Determinar si estamos en UCPList (ruta index)
  const isUCPList = location.pathname === "/v2/ucp" || location.pathname.endsWith("/ucp");

  // Ref to read current isChatOpen in cleanup without adding it to deps (would re-run on close).
  const isChatOpenRef = useRef(isChatOpen);
  isChatOpenRef.current = isChatOpen;

  // Register UCPPage as the chat host only when a contact is open (has native chat).
  // On the list page (/v2/ucp) we unregister so the global floating panel opens instead.
  // When leaving UCP contact: if chat was open, transfer state to global panel so it stays
  // open on the destination page (except Home, which has its own chat and will auto-close).
  useEffect(() => {
    if (isUCPList) {
      unregisterChatHost();
      return;
    }
    registerChatHost({
      open: openUCPChat,
      close: () => setIsChatOpen(false),
    });
    return () => {
      unregisterChatHost();
      if (isChatOpenRef.current) openGlobalChat();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUCPList, registerChatHost, unregisterChatHost, openGlobalChat, openUCPChat]);

  const handleOpenChat = openUCPChat;

  const handleCloseChat = useCallback(() => {
    setIsChatOpen(false);
  }, []);

  return (
    <ContainerWrapper>
      <UCPPageContainer>
        <Routes>
          <Route index element={<UCPList />} />
          <Route path="test-email" element={
            <SectionShell onOpenChat={handleOpenChat}>
              <TestEmail />
            </SectionShell>
          } />
          <Route path=":contactId">
            <Route path="overview/*" element={
              <SectionShell onOpenChat={handleOpenChat}>
                <Overview />
              </SectionShell>
            } />
            <Route path="activity/*" element={
              <SectionShell onOpenChat={handleOpenChat}>
                <Notes />
              </SectionShell>
            } />
            <Route path="snapshot" element={
              <SectionShell onOpenChat={handleOpenChat}>
                <Snapshot />
              </SectionShell>
            } />
            <Route path="garage" element={
              <SectionShell onOpenChat={handleOpenChat}>
                <Garage />
              </SectionShell>
            } />
            <Route path="appointments" element={
              <SectionShell onOpenChat={handleOpenChat}>
                <Appointments />
              </SectionShell>
            } />
            <Route path="repairs-orders" element={
              <SectionShell onOpenChat={handleOpenChat}>
                <RepairsOrders />
              </SectionShell>
            } />
            <Route path="tasks" element={
              <SectionShell onOpenChat={handleOpenChat}>
                <Tasks />
              </SectionShell>
            } />

          </Route>
          
        </Routes>
      </UCPPageContainer>
      {!isUCPList && hasOpenedChat && (
        <UCPChatWrapper $isOpen={isChatOpen}>
          <ChatErrorBoundary>
            <ChatSlideOut isOpen={isChatOpen} onClose={handleCloseChat} onOpenInHome={handleOpenInHome} />
          </ChatErrorBoundary>
        </UCPChatWrapper>
      )}
    </ContainerWrapper>
  );
};

export default UCPPage;
